import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CreditCard, Lock, CheckCircle, ArrowLeft, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from '../hooks/use-toast';
import TrustBadges from '../components/TrustBadges';
import api from '../services/api';
import analytics from '../services/analytics';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
  });

  const subtotal = getCartTotal();
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + tax;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Your Cart is Empty</h2>
            <p className="text-slate-600 mb-6">Add some products to checkout.</p>
            <Button onClick={() => navigate('/shop')}>Continue Shopping</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions to continue.",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);

    // Track checkout start
    analytics.trackBeginCheckout(cartItems, total);

    try {
      // Step 1: Create order in backend
      const orderData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        items: cartItems.map(item => ({
          productId: item.id,
          quantity: item.quantity
        })),
        paymentMethod: 'stripe'
      };

      const orderResponse = await api.createOrder(orderData);
      
      // Step 2: Create Stripe checkout session
      const checkoutResponse = await api.createCheckoutSession(orderResponse.orderId);
      
      // Step 3: Store order info in localStorage for after payment
      localStorage.setItem('pending_order', JSON.stringify({
        orderId: orderResponse.orderId,
        orderNumber: orderResponse.orderNumber,
        email: formData.email,
        total: total,
        items: cartItems
      }));
      
      // Step 4: Clear cart before redirecting to Stripe
      clearCart();
      
      // Step 5: Redirect to Stripe checkout
      window.location.href = checkoutResponse.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Checkout Failed",
        description: error.response?.data?.detail || "There was an error processing your order. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-full mx-auto px-8 lg:px-16">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cart
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Checkout</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          required
                          className="mt-1"
                          data-testid="checkout-email"
                        />
                        <p className="text-xs text-gray-500 mt-1">Download links will be sent here</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Billing Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Billing Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                          data-testid="checkout-firstname"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                          data-testid="checkout-lastname"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Payment Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2" />
                      Payment Method
                    </h3>
                    <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-lg p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-white p-2 rounded-lg shadow-sm">
                          <svg className="h-8 w-auto" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
                            <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.64 10.64 0 0 1-4.56 1.02c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.56zm-3.94-3.24c0-1.14-.56-2.45-1.82-2.45-1.28 0-1.96 1.23-2.14 2.45h3.96zM49.97 21.28h-4.6V10.67h4.6v10.61zm0-12.26h-4.6V5.65h4.6v3.37zm-6.39 12.26h-4.6v-5.97c0-1.33-.05-3.13-1.91-3.13-1.91 0-2.21 1.49-2.21 3.03v6.07h-4.6V10.67h4.42v1.45h.06c.61-1.16 2.11-2.38 4.35-2.38 4.65 0 5.49 3.06 5.49 7.03v4.51zm-14.94 0h-4.6v-5.97c0-1.33-.05-3.13-1.91-3.13-1.91 0-2.21 1.49-2.21 3.03v6.07h-4.6V10.67h4.42v1.45h.06c.61-1.16 2.11-2.38 4.35-2.38 4.65 0 5.49 3.06 5.49 7.03v4.51z" fill="#635BFF"/>
                            <path d="M13.65 7.54c0-1.65-1.36-2.32-3.22-2.32H6.45v11.55h2.68v-4.3h1.02l2.41 4.3h3.04l-2.82-4.73c1.4-.61 2.87-1.74 2.87-4.5zm-4.52 2.82H9.13V7.33h.68c.89 0 2.03.1 2.03 1.42 0 1.19-.78 1.61-2.03 1.61h-.68z" fill="#635BFF"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Secure Stripe Checkout</p>
                          <p className="text-sm text-slate-600">You&apos;ll be redirected to complete payment</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Lock className="h-4 w-4 text-teal-600" />
                        <span>Your payment info is encrypted and secure</span>
                      </div>
                    </div>
                  </div>

                  {/* Terms Acceptance */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        data-testid="accept-terms"
                      />
                      <span className="text-sm text-gray-700">
                        I agree to the{' '}
                        <a href="/page/terms" target="_blank" className="text-teal-600 hover:underline">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/page/privacy" target="_blank" className="text-teal-600 hover:underline">
                          Privacy Policy
                        </a>
                        . I understand that I am purchasing digital products and downloads will be available immediately after payment.
                      </span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700 py-6 text-lg"
                    disabled={isProcessing || !acceptedTerms}
                    data-testid="checkout-submit"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock className="h-5 w-5 mr-2" />
                        Proceed to Payment - ${total.toFixed(2)}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Cart Items */}
                  <div className="space-y-3">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium line-clamp-2">{item.name}</h4>
                          <p className="text-sm text-slate-600">Qty: {item.quantity}</p>
                        </div>
                        <div className="text-sm font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Price Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="font-medium">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tax (8%)</span>
                      <span className="font-medium">${tax.toFixed(2)}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-teal-600">${total.toFixed(2)}</span>
                  </div>

                  {/* Trust Badges */}
                  <TrustBadges variant="compact" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
